import React, { memo, useEffect, useState } from "react";
import { Header } from "../header/header";
import { MainContent } from "../main-content/main-content";
import styled, { ThemeContext } from "styled-components";
import { useParams, useLocation } from "react-router-dom";
import { requestParamsImpl } from "../../services/request-params.service";

import { moduleSettings } from "../../stores/module-settings.store";
import { ApiErrorView } from "../errors/api-error-view";
import { LoaderDots } from "../loader/loader-dots";
import { Footer } from "../footer/footer";
import { ApiError } from "../../services/api-errors";
import { UrlParams } from "../../types";
import { useTranslation } from "react-i18next";
import { ThemeManager } from "../../theme-manager/theme-manager";
import { config } from "../../config";
import facebookPixelService from "../../services/facebook-pixel.service";
import WebFont from "webfontloader";
// @ts-ignore
import { CookieBanner } from "@palmabit/react-cookie-law";
import App from "../../App";
import { createTheme, MuiThemeProvider } from "@material-ui/core";
import { UpdateFromBookingErrorImpl } from "../../services/api-errors/api-errors-impl";
import i18n from "../../i18n";
import { buildRouteWithParams } from "../../helpers/build-route-with-params";
import { selectionStore } from "../../stores/selection.store";

interface IStyled {
  bodyBackgrounColor?: string;
  backgroundImage?: string;
  fontFamily?: string;
}

export const MainLayoutWrapper = styled.div<IStyled>`
  position: relative;
  width: 100%;
  min-height: calc(100% - 60px);
  background: ${(props) =>
    props.backgroundImage
      ? `url(${props.backgroundImage}) no-repeat center center fixed`
      : props.bodyBackgrounColor};
  background-size: cover;
  font-family: ${(props) => props.fontFamily};
`;

const MainLayoutDef = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const params: any = useParams();
  const searchParams = new URLSearchParams(location.search);
  const hashParams = new URLSearchParams(location.hash.replace("#", "?"));
  const [moduleInitialized, setModuleInitialized] = useState(false);
  //const [moduleIsRefreshing, setModuleIsRefreshing] = useState(false);
  const [apiError, setApiError] = useState<ApiError>();
  const urlParams = useParams<UrlParams>();
  const backgroundImageUrl = moduleSettings.backgroundImageUrl;
  const overwrittenBackgroundColor =
    requestParamsImpl.overwrittenBackgroundColor();
  let bodyBackgroundColor = moduleSettings.bodyBackgroundColor;
  if (overwrittenBackgroundColor) {
    bodyBackgroundColor = overwrittenBackgroundColor;
  } else if (backgroundImageUrl) {
    bodyBackgroundColor = "transparent";
  }

  const [redirectToHome, setRedirectToHome] = useState(false);

  if (location.pathname.includes("update_from_email")) {
    config.routes.base = `/instabook/bookings/${params.bookingId}/update_from_email`;
  }

  const fontFamily = moduleSettings.fontFamily;

  const muiTheme = createTheme({
    typography: {
      fontFamily: fontFamily,
    },
    palette: {
      secondary: {
        main: "#808080",
      },
    },
    overrides: {
      MuiInputLabel: {
        root: {
          fontSize: 12,
        },
      },
      MuiFormControlLabel: {
        label: {
          fontSize: 12,
        },
      },
    },
  });

  useEffect(() => {
    WebFont.load({
      google: {
        families: [fontFamily],
      },
    });
  }, [moduleInitialized]);

  requestParamsImpl.set(urlParams, searchParams, hashParams);

  useEffect(() => {
    moduleSettings.initModuleSettings().subscribe(
      () => {
        setModuleInitialized(true);
        moduleSettings.moduleInitialized = true;
        if (location.pathname.includes("update_from_email")) {
          if (moduleSettings.isUpdateFromBookingError) {
            const e: UpdateFromBookingErrorImpl =
              new UpdateFromBookingErrorImpl(
                moduleSettings.computeUpdateFromBookingErrorMessage(
                  i18n.language
                )!
              );
            setApiError(e);
          }
        }
        document.title =
          moduleSettings.getRestaurantName() + " - " + t("instabook.booking");
      },
      (e: ApiError) => {
        setApiError(e);
      }
    );
  }, []);

  if (redirectToHome) {
    return <App />;
  }

  if (!moduleInitialized && !apiError) {
    return <LoaderDots />;
  }

  const content = apiError ? (
    <ApiErrorView error={apiError} isLayout={moduleInitialized} />
  ) : (
    <MainContent />
  );
  const onHeaderClick = () => {
    if (location.pathname.includes("update_from_email")) {
      config.routes.base = "/instabook/bookings";
      window.location.href = buildRouteWithParams(
        config.routes.selection,
        false
      );
    }
    selectionStore.resetStore();
    setRedirectToHome(true);
  };
  return (
    <MuiThemeProvider theme={muiTheme}>
      <MainLayoutWrapper
        data-test="main-content"
        bodyBackgrounColor={bodyBackgroundColor}
        backgroundImage={backgroundImageUrl}
        fontFamily={fontFamily}
        id="main-content"
      >
        <ThemeContext.Provider value={ThemeManager.getTheme()}>
          {moduleInitialized && <Header onHeaderClick={onHeaderClick} />}
          {content}
        </ThemeContext.Provider>
      </MainLayoutWrapper>
      <Footer />
      {moduleInitialized && moduleSettings.hasFacebookPixel() && (
        <CookieBanner
          message={
            <span
              dangerouslySetInnerHTML={{
                __html: t("instabook.cookie_banner.text"),
              }}
            />
          }
          wholeDomain={true}
          privacyPolicyLinkText={t("instabook.cookie_banner.policy_text")}
          necessaryOptionText={t(
            "instabook.cookie_banner.necessary_option_text"
          )}
          preferencesOptionText={t(
            "instabook.cookie_banner.preferences_option_text"
          )}
          statisticsOptionText={t(
            "instabook.cookie_banner.statistics_option_text"
          )}
          marketingOptionText={t(
            "instabook.cookie_banner.marketing_option_text"
          )}
          acceptButtonText={t("instabook.cookie_banner.accept_button_text")}
          declineButtonText={t("instabook.cookie_banner.decline_button_text")}
          managePreferencesButtonText={t(
            "instabook.cookie_banner.manage_preferences_button_text"
          )}
          savePreferencesButtonText={t(
            "instabook.cookie_banner.save_preferences_button_text"
          )}
          preferencesDefaultChecked={false}
          statisticsDefaultChecked={false}
          marketingDefaultChecked={false}
          onAcceptStatistics={() => {
            facebookPixelService.grantConsent();
          }}
          onDeclineStatistics={() => {
            facebookPixelService.revokeConsent();
          }}
          styles={{
            dialog: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              backgroundColor: "white",
              zIndex: 10000,
              padding: "10px",
            },
          }}
        />
      )}
    </MuiThemeProvider>
  );
};

export const MainLayout = memo(MainLayoutDef);
